import { dayjs, ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { ContractServices, OrderDirection } from 'network'
import { useBaseInfoStore, useDoubleContractAssetsAccountStore, useExchangeInfoStore, useLoadMore, useProjectTheme, useResponsive, useUserCenterContractOrderStore } from 'store'
import { DefineComponentProps } from 'utils'
import { ArrowRight, EmptyData, FullScreenMinHeight, HocElTable, LoadingComponent, LoadMore, Pnl, Tag, UiUtils, VirtualList } from '../index'
import { OrderListLayout } from '../layouts/Order.layout'
import { HistoricalEntrustItem } from './HistoricalEntrustItem'

export type HistoricalEntrustProps = {
  positionId?: string
  emptyAreaHeight?: number
}

export const HistoricalEntrust = defineComponent<HistoricalEntrustProps>({
  props: DefineComponentProps<HistoricalEntrustProps>(['positionId', 'emptyAreaHeight']),
  setup(props) {
    const contractOrderListStore = useUserCenterContractOrderStore()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const responsive = useResponsive()
    const { t } = useI18n()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const doubleAccount = useDoubleContractAssetsAccountStore()

    const historicalEntrustStore = useLoadMore<ApiResponse.HistoricalCommission>({
      isInitFetch: false,
      fetcher: ContractServices.getHistoricalCommission.bind(ContractServices),
      requestInterceptor: () => {
        return doubleAccount.openContractFeature === '1'
      },
      handleParams: (params) => {
        return { ...(params || {}), positionId: props.positionId }
      },
    })

    onBeforeUnmount(() => {
      contractOrderListStore.$dispose()
    })

    const openEntrustDetail = (row: ApiResponse.HistoricalCommission) => {
      UiUtils.showEntrustDetail({
        trustId: row.trustId.toString(),
        entrust: row,
      })
    }

    watch(
      () => doubleAccount.doubleContractAssetsAccount?.openFutures,
      (openFutures) => {
        if (openFutures) {
          historicalEntrustStore.initFetch()
        }
      },
      {
        immediate: true,
      },
    )

    return () => {
      if (responsive.isMobile) {
        return (
          <LoadMore
            loading={historicalEntrustStore.loadMoreLoading.value}
            noMore={!historicalEntrustStore.hasMore.value}
            onLoadMore={historicalEntrustStore.loadMore}
            isEmpty={!historicalEntrustStore.dataSource.value.length}
          >
            <VirtualList itemMinHeight={234} data={historicalEntrustStore.dataSource.value} noRenderWhenNotInView={false}>
              {{
                default: ({ data, index }: { data: ApiResponse.HistoricalCommission; index: number }) => {
                  return <HistoricalEntrustItem data={data} divider={index !== historicalEntrustStore.dataSource.value.length - 1} />
                },
              }}
            </VirtualList>
          </LoadMore>
        )
      }
      return (
        <OrderListLayout class="has-cursor" style={{ padding: 0 }}>
          <LoadingComponent loading={historicalEntrustStore.loading.value}>
            <LoadMore
              loading={historicalEntrustStore.loadMoreLoading.value}
              noMore={!historicalEntrustStore.hasMore.value}
              isEmpty={historicalEntrustStore.dataSource.value.length === 0}
              onLoadMore={historicalEntrustStore.loadMore}
            >
              <HocElTable data={historicalEntrustStore.dataSource.value} onRow-click={(row: ApiResponse.HistoricalCommission) => openEntrustDetail(row)}>
                {{
                  default: () => (
                    <>
                      <ElTableColumn label={t('币种')} minWidth={220}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            return (
                              <div class={'flex align-center gap-row-8'}>
                                <img width={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(row.symbolId)} alt="" />
                                <div class={'symbol-info'}>
                                  <span class="symbol-name">
                                    {exchangeStore.getContractSymbolName(row.symbolId)} {t('永续')}
                                    <ArrowRight />
                                  </span>
                                  <div class="flex info-tags gap-4">
                                    <Tag color={row.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{contractOrderListStore.getOrderType(row)}</Tag>
                                    <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                      {t(contractOrderListStore.getEntrustType(row))}
                                    </Tag>
                                    <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                      {row.marginType === 1 ? t('逐仓') : t('全仓')}
                                      {row.leverage}X
                                    </Tag>
                                  </div>
                                </div>
                              </div>
                            )
                          },
                        }}
                      </ElTableColumn>
                      <ElTableColumn prop="rlzPnl" label={t('已实现盈亏')} minWidth={180}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            return <Pnl value={row.rlzPnl}>USDT</Pnl>
                          },
                        }}
                      </ElTableColumn>
                      <ElTableColumn prop="status" label={t('状态')} minWidth={180}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            const { status, statusColor } = contractOrderListStore.getHistoricalCommissionStatus(row)
                            return <Tag color={statusColor}>{status}</Tag>
                          },
                        }}
                      </ElTableColumn>
                      <ElTableColumn prop="qty" label={t('委托总量') + '(USDT)'} minWidth={180}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            return `${contractOrderListStore.getCommissionQty(row).formatNumberWithCommas()}`
                          },
                        }}
                      </ElTableColumn>
                      <ElTableColumn prop="qty" label={t('已成交总量') + '(USDT)'} minWidth={180}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            return `${contractOrderListStore.getDealInterestQty(row).formatNumberWithCommas()}`
                          },
                        }}
                      </ElTableColumn>
                      <ElTableColumn prop="avgPrice" label={t('成交均价') + '(USDT)'} minWidth={180}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            return `${row.avgPrice.formatNumberWithCommas()}`
                          },
                        }}
                      </ElTableColumn>
                      <ElTableColumn prop="qty" label={t('创建时间')} minWidth={180}>
                        {{
                          default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                            return dayjs(row.createTime).format('YYYY-MM-DD HH:mm:ss')
                          },
                        }}
                      </ElTableColumn>
                    </>
                  ),
                  empty: () => {
                    return <FullScreenMinHeight emptyAreaHeight={props.emptyAreaHeight}>{!historicalEntrustStore.loading.value && <EmptyData />}</FullScreenMinHeight>
                  },
                }}
              </HocElTable>
            </LoadMore>
          </LoadingComponent>
        </OrderListLayout>
      )
    }
  },
})
